import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { twJoin } from '../../../../utils';
import { LayoutFeature, LayoutNavigationItem } from '../../layout.model';
import { getActiveChild } from '../../layout.utils';
import { IdGeneratorService } from '@lib/fresco';
import { NavIconComponent } from './nav-icon.component';
import { NgIf, NgFor } from '@angular/common';
import { IconComponent } from '../../../../icons/icon.component';
import { NavItemComponent } from './nav-item.component';
import { filter, map, Subscription } from 'rxjs';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'da-nav-disclosure',
  template: `
    <div>
      <button
        type="button"
        [id]="toggleId"
        [attr.aria-controls]="popupId"
        [attr.aria-expanded]="open"
        [class]="
          twJoin(
            'tw-flex tw-w-full tw-items-center tw-px-4 tw-py-2 tw-text-left rtl:tw-text-right',
            'hover:tw-bg-[rgb(from_var(--apollo-layout-highlight)_r_g_b_/_15%)] hover:tw-text-[var(--apollo-layout-highlight)]',
            'focus:tw-bg-[rgb(from_var(--apollo-layout-highlight)_r_g_b_/_15%)] focus:tw-text-[var(--apollo-layout-highlight)] focus:tw-outline-none',
            'active:tw-bg-[rgb(from_var(--apollo-layout-highlight)_r_g_b_/_30%)] active:tw-text-[var(--apollo-layout-highlight)]'
          )
        "
        [attr.data-dgat]="dgat"
        [attr.hidden]="visibleChildren.length === 0"
        (click)="open = !open"
      >
        <da-nav-icon
          [item]="item"
          [active]="!!activeChild"
          className="tw-size-6 tw-shrink-0 tw-mr-2 rtl:tw-mr-0 rtl:tw-ml-2"
        />

        <span [class]="twJoin(beta?.enabled && item.isBeta ? '' : 'tw-grow')">{{
          item.text
        }}</span>

        <div
          *ngIf="beta?.enabled && item.isBeta"
          [class]="twJoin('tw--mt-2 tw-flex tw-grow tw-self-start')"
        >
          <div
            [class]="
              twJoin(
                'tw-rounded-2xl tw-border tw-border-purple-300 tw-bg-purple-100 tw-px-1 tw-py-0.5 tw-text-[8px] tw-font-extrabold tw-uppercase tw-leading-3 tw-text-purple-800'
              )
            "
          >
            {{ beta.text }}
          </div>
        </div>

        <da-icon
          icon="chevron-right"
          type="solid"
          [solidSize]="16"
          [className]="
            twJoin(
              'tw-size-4 tw-ml-2 rtl:tw-ml-0 rtl:tw-mr-2',
              open ? 'tw-rotate-90' : 'rtl:tw-rotate-180'
            )
          "
        />
      </button>

      <div [style.display]="open ? 'block' : 'none'">
        <ul
          class="tw-flex tw-flex-col"
          [id]="popupId"
          [attr.aria-labeledby]="toggleId"
        >
          <li *ngFor="let child of visibleChildren">
            <ng-container *ngIf="child.href || child.routerLink">
              <da-nav-item
                [item]="child"
                [active]="child === activeChild"
                (itemSelect)="handleItemSelect(child)"
                className="tw-px-12 tw-text-base"
              ></da-nav-item>
            </ng-container>
          </li>
        </ul>
      </div>

      <ul
        *ngIf="
          !open && activeChild && (activeChild.href || activeChild.routerLink)
        "
      >
        <li>
          <da-nav-item
            [item]="activeChild"
            [active]="true"
            (itemSelect)="handleItemSelect(activeChild)"
            className="tw-px-12 tw-text-base"
          ></da-nav-item>
        </li>
      </ul>
    </div>
  `,
  standalone: true,
  imports: [NavIconComponent, NgIf, IconComponent, NgFor, NavItemComponent],
})
export class NavDisclosureComponent implements OnInit {
  @Input() public item!: LayoutNavigationItem;
  @Input() public beta?: LayoutFeature;
  @Output() public itemSelect = new EventEmitter<LayoutNavigationItem>();

  public open = false;
  public activeChild: LayoutNavigationItem | null = null;
  public twJoin = twJoin;
  public toggleId: string;
  public popupId: string;
  public dgat: string;

  private subscription: Subscription;

  public get visibleChildren() {
    const isChildVisible = (child) => child.visible;
    return this.item.children.filter(isChildVisible);
  }

  constructor(
    private router: Router,
    private idGenerator: IdGeneratorService,
    private cdr: ChangeDetectorRef
  ) {
    this.watchPathname();
  }

  public ngOnInit(): void {
    this.updateActiveChild(this.router.url);
    this.toggleId = this.idGenerator.generateStringId('nav-menu-toggle');
    this.popupId = this.idGenerator.generateStringId('nav-popup-menu');

    /*  
      This is not a great solution, instead what we probably want to do is not have `sidebar` in the dgat, and instead it should 
      be a token that we can replace depending on if we're showing. Tracking that effort as tech debt (PD-114161) to follow up 
      on after MPR. 
    */
    this.dgat = this.item.dgat.replace('sidebar', 'responsive-header-menu');
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public handleItemSelect(item: LayoutNavigationItem) {
    this.open = false;
    item.trackEvent?.();
    this.itemSelect.emit(item);
  }

  private updateActiveChild(url: string) {
    this.activeChild = getActiveChild(url, this.item.children);
  }

  /**
   * When route changes update active child if applicable
   */
  private watchPathname(): void {
    this.subscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => (event as NavigationEnd).urlAfterRedirects)
      )
      .subscribe(() => {
        this.updateActiveChild(this.router.url);
        this.cdr.detectChanges();
      });
  }
}
